import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

// Numune Bırak formu — kaydetmeden ÖNCE fotoğraf seçme/çekme alanı.
// Fotoğraflar client-side küçültülür (lib/imageCompress — kargo fotoğraflarıyla
// aynı mevcut util, 1600px/JPEG), thumbnail önizlenir, numune kaydedildikten
// sonra mevcut /samples/{id}/photos endpoint'ine yüklenir.
// Backend limitiyle aynı: max 5 fotoğraf.
const MAX_PHOTOS = 5;

const fmtKB = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function PendingSamplePhotos({ files, onChange, disabled = false }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const addFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const remaining = MAX_PHOTOS - files.length;
    if (remaining <= 0) {
      toast.error(`En fazla ${MAX_PHOTOS} fotoğraf eklenebilir`);
      return;
    }
    const picked = Array.from(fileList).slice(0, remaining);
    setBusy(true);
    const added = [];
    for (const f of picked) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: görsel değil`); continue; }
      try {
        const small = await compressImage(f, { quality: 0.78 });
        added.push({
          _lid: Math.random().toString(36).slice(2),
          file: small,
          url: URL.createObjectURL(small),
          origSize: f.size,
          newSize: small.size,
        });
      } catch { toast.error(`${f.name}: işlenemedi`); }
    }
    setBusy(false);
    if (added.length > 0) onChange([...files, ...added]);
    if (camRef.current) camRef.current.value = "";
    if (galRef.current) galRef.current.value = "";
  };

  const remove = (lid) => {
    const target = files.find((f) => f._lid === lid);
    if (target) URL.revokeObjectURL(target.url);
    onChange(files.filter((f) => f._lid !== lid));
  };

  return (
    <div className="space-y-2" data-testid="pending-photos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-slate-200 flex items-center gap-1.5">
          <Camera className="w-4 h-4 text-slate-400" /> Numune Fotoğrafları ({files.length}/{MAX_PHOTOS})
          <span className="text-[10px] text-slate-500 font-normal">— opsiyonel · yüklenmeden önce otomatik küçültülür</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Kamera: sadece capture'lı input — mobilde arka kamera açılır */}
          <input
            ref={camRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="photo-camera-input"
            onChange={(e) => addFiles(e.target.files)}
          />
          {/* Galeri/dosya: capture YOK — kullanıcı galeriden seçer */}
          <input
            ref={galRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="photo-gallery-input"
            onChange={(e) => addFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => camRef.current?.click()}
            disabled={disabled || busy || files.length >= MAX_PHOTOS}
            className="rounded-sm text-xs border-slate-700"
            data-testid="photo-camera-btn"
          >
            <Camera className="w-3.5 h-3.5 mr-1" /> Kameradan Çek
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => galRef.current?.click()}
            disabled={disabled || busy || files.length >= MAX_PHOTOS}
            className="rounded-sm text-xs border-slate-700"
            data-testid="photo-gallery-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
            Cihazdan Seç
          </Button>
        </div>
      </div>
      {files.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {files.map((p) => (
            <div key={p._lid} className="relative group border border-slate-800 rounded-sm overflow-hidden bg-slate-950 aspect-square" data-testid={`pending-photo-${p._lid}`}>
              <img src={p.url} alt={p.file.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(p._lid)}
                className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-500 text-white p-1 rounded-sm"
                data-testid={`pending-photo-delete-${p._lid}`}
                aria-label="Fotoğrafı kaldır"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-slate-950/80 text-[9px] font-mono text-slate-400 px-1.5 py-0.5 truncate">
                {fmtKB(p.origSize)} → {fmtKB(p.newSize)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
